import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { useInviteClientMember } from '../hooks/useInviteClientMember';
import { useToast } from '@/shared/hooks/useToast';
import { createInviteClientMemberSchema, type InviteFormValues } from '../schemas';

interface InviteClientMemberDialogProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteClientMemberDialog({
  clientId,
  open,
  onOpenChange,
}: InviteClientMemberDialogProps) {
  const { t } = useTranslation('client-members');
  const inviteSchema = createInviteClientMemberSchema(t);
  const inviteMember = useInviteClientMember(clientId);
  const { toast } = useToast();

  const { control, handleSubmit, reset } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: InviteFormValues) => {
    try {
      await inviteMember.mutateAsync({
        email: data.email,
        role: 'client',
      });

      toast.success({
        title: t('toast.inviteSent'),
        description: t('toast.inviteSentDesc', { email: data.email }),
      });

      reset();
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-none rounded-none md:max-w-105 md:rounded-lg">
        <DialogHeader>
          <DialogTitle>{t('invite.title')}</DialogTitle>
          <DialogDescription>{t('invite.description')}</DialogDescription>
        </DialogHeader>

        <form
          id="invite-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 py-2"
        >
          <FormFieldInput
            control={control}
            name="email"
            label={t('invite.email')}
            type="email"
            floatingLabel
            data-testid="client-members-invite-email-input"
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            data-testid="client-members-invite-cancel-button"
          >
            {t('invite.cancel')}
          </Button>
          <Button
            type="submit"
            form="invite-form"
            disabled={inviteMember.isPending}
            data-testid="client-members-invite-submit-button"
          >
            {inviteMember.isPending ? t('invite.submitting') : t('invite.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
